import { buildConfigPointer, currentUser, pointer, pointerId, queryClass, saveClassRecord } from './parse';
import { loadMyCustomer } from './account';

export type VideoProgressEntry = { id: string; time: number; totalTime: number; updatedAt?: string };

// Progress kept on-device is scoped per user so shared browsers never leak between accounts.
function localProgressKey(): string {
  return `ayurguide-v3-progress-local:${pointerId(currentUser()) ?? 'anon'}`;
}
// Mirrors the native app: one elearning_Bookmarks row per user holding a Bookmarks array.
type BookmarksRow = { objectId?: string; Bookmarks?: VideoProgressEntry[]; CustomerId?: unknown; UserId?: unknown };

function realUserId(): string | undefined {
  const id = pointerId(currentUser());
  return id && id !== 'local-student' ? id : undefined;
}

export function readLocalProgressEntries(): VideoProgressEntry[] {
  localStorage.removeItem('ayurguide-v3-progress-local'); // purge pre-user-scoping shared data
  try { return JSON.parse(localStorage.getItem(localProgressKey()) || '[]'); } catch { return []; }
}

function writeLocalProgress(entries: VideoProgressEntry[]) {
  localStorage.setItem(localProgressKey(), JSON.stringify(entries.slice(-500)));
}

async function loadServerRow(): Promise<BookmarksRow | undefined> {
  const userId = realUserId();
  if (!userId) return undefined;
  const customer = await loadMyCustomer().catch(() => undefined);
  const identities = [
    ...(customer?.objectId ? [pointer('elearning_Customer', customer.objectId)] : []),
    pointer('_User', userId),
  ];
  for (const identity of identities) {
    const rows = await queryClass('elearning_Bookmarks', {
      limit: 1,
      appScoped: false,
      keys: ['Bookmarks', 'CustomerId', 'UserId'],
      where: (query) => {
        query.equalTo('AppId', buildConfigPointer());
        query.equalTo('CustomerId', identity);
        query.equalTo('UserId', pointer('_User', userId));
      },
    }) as BookmarksRow[];
    if (rows[0]) return rows[0];
  }
  for (const identity of identities) {
    const rows = await queryClass('elearning_Bookmarks', {
      limit: 1,
      appScoped: false,
      keys: ['Bookmarks', 'CustomerId', 'UserId'],
      where: (query) => {
        query.equalTo('AppId', buildConfigPointer());
        query.equalTo('CustomerId', identity);
      },
    }) as BookmarksRow[];
    if (rows[0]) return rows[0];
  }
  return { Bookmarks: [], CustomerId: identities[0] };
}

/** Resolve saved playback position for one video (server first, local fallback). */
export async function loadVideoProgress(videoId: string): Promise<VideoProgressEntry | undefined> {
  if (realUserId()) {
    try {
      const row = await loadServerRow();
      const entry = row?.Bookmarks?.find((item) => item.id === videoId);
      if (entry) return entry;
    } catch { /* fall through to local copy */ }
  }
  return readLocalProgressEntries().find((item) => item.id === videoId);
}

/** Persist playback position; elearning_Bookmarks for real students, localStorage otherwise. */
export async function saveVideoProgress(videoId: string, time: number, totalTime: number): Promise<void> {
  const entry: VideoProgressEntry = { id: videoId, time, totalTime, updatedAt: new Date().toISOString() };
  const userId = realUserId();
  if (!userId) {
    writeLocalProgress([...readLocalProgressEntries().filter((item) => item.id !== videoId), entry]);
    return;
  }
  try {
    const row = await loadServerRow();
    const entries = [...(row?.Bookmarks ?? []).filter((item) => item.id !== videoId), entry];
    const customerId = row?.CustomerId ?? pointer('_User', userId);
    const ownedByCurrentUser = pointerId(row?.UserId) === userId;
    await saveClassRecord('elearning_Bookmarks', { Bookmarks: entries, CustomerId: customerId }, ownedByCurrentUser ? row?.objectId : undefined);
    writeLocalProgress(entries);
  } catch {
    writeLocalProgress([...readLocalProgressEntries().filter((item) => item.id !== videoId), entry]);
  }
}
